import { showGuestProjectWithImageOnly } from '@/core/constants/appConfig';
import { useTranslation } from '@/core/hooks';
import { useMemo } from 'react';
import {
  filterGuestProjectsForListing,
  mapProjectToCard,
  sortProjectsByContentfulOrder,
} from '../services/mapGuestProjects';
import type {
  GuestListLoadState,
  GuestPropertyCardModel,
} from '../types/guest.types';
import { useGuestLabels } from './useGuestLabels';
import {
  useGuestProjectAssets,
  type GuestContentfulFetchSnapshot,
} from './useGuestProjectImages';
import { useGuestProjectsQuery } from './useGuestProjectsQuery';

export interface UseGuestPropertiesListResult {
  readonly loadState: GuestListLoadState;
  readonly properties: ReadonlyArray<GuestPropertyCardModel>;
  readonly canLoadMore: boolean;
  readonly isFetchingNextPage: boolean;
  /** True while background catalogue pages are still loading. */
  readonly isPrefetchingCatalogue: boolean;
  readonly loadMore: () => void;
  /** Guest-tag Contentful fetch snapshot (home-page logging). */
  readonly contentfulFetch: GuestContentfulFetchSnapshot | null;
  readonly isContentfulSuccess: boolean;
}

/** Guest property list — paginated /v1/projects + Contentful images/order. */
export function useGuestPropertiesList(): UseGuestPropertiesListResult {
  const labels = useGuestLabels();
  const { currentLanguage } = useTranslation();
  const locale = currentLanguage === 'ar' ? 'ar' : 'en';
  const {
    projects,
    isLoading,
    isError,
    canLoadMore,
    isFetchingNextPage,
    isPrefetchingCatalogue,
    loadMore,
  } = useGuestProjectsQuery();
  const {
    imagesByProjectId,
    nameByProjectId,
    orderByProjectId,
    contentfulFetch,
    isContentfulSuccess,
    isContentfulPending,
  } = useGuestProjectAssets();

  const properties = useMemo(() => {
    const sortedProjects = sortProjectsByContentfulOrder(
      projects,
      orderByProjectId,
    );
    const visibleProjects = filterGuestProjectsForListing(
      sortedProjects,
      imagesByProjectId,
      showGuestProjectWithImageOnly,
    );
    return visibleProjects.map((project) =>
      mapProjectToCard(
        project,
        labels.unitsBadge,
        imagesByProjectId[project.project_id] ?? [],
        {
          contentfulName: nameByProjectId[project.project_id],
          locale,
        },
      ),
    );
  }, [
    projects,
    labels.unitsBadge,
    imagesByProjectId,
    nameByProjectId,
    orderByProjectId,
    locale,
  ]);

  // Image filter depends on Contentful. Hold the skeleton until that fetch
  // settles so an empty image map is not treated as an empty catalogue.
  const waitingForContentfulImages =
    showGuestProjectWithImageOnly && isContentfulPending;

  let loadState: GuestListLoadState = 'ready';
  if (isError) {
    loadState = 'error';
  } else if (isLoading || waitingForContentfulImages) {
    loadState = 'loading';
  } else if (properties.length === 0) {
    loadState = 'empty';
  }

  return {
    loadState,
    properties,
    canLoadMore,
    isFetchingNextPage,
    isPrefetchingCatalogue,
    loadMore,
    contentfulFetch,
    isContentfulSuccess,
  };
}
